import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Paper, Tabs, Tab, Switch, FormControlLabel, Button, TextField, IconButton, List, ListItem, ListItemButton, Typography, Popper, CircularProgress, useMediaQuery, } from '@mui/material';
import { Dropdown } from '../FormInputs';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getLocalTimeZone, today, CalendarDate, startOfMonth, endOfMonth, startOfWeek, } from '@internationalized/date';
import './CustomDatePicker.css';
/** Label of the opt-in Custom Range preset. */
const CUSTOM_RANGE = 'Custom Range';
import { useSelector, useDispatch } from 'react-redux';
import { selectHolidays, selectHolidaysLoading } from '../../redux/userDataSlice';
import AppButton from '../AppButton';
import { selectToken } from '../../redux/authSlice';
import { ApiCall } from '../../helper/axios';
import { useAccountOverview } from '../../context/AccountOverviewContext';
const CustomDatePicker = ({ value = null, compareValue = null, onChange = () => { }, onCompareChange = () => { }, placeholder = 'Select Date Range', comparePlaceholder = 'Select Compare Range', onApply, comparison = true, maxPastYears = 2, maxNextYears = 0, size = 'default', disabled = false, allowAllDates = false, allowToday = false, popperStrategy = 'absolute', clientId, applyLabel = 'Apply', }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [compareEnabled, setCompareEnabled] = useState(!!compareValue);
    const [tempDateRange, setTempDateRange] = useState(value);
    const [tempCompareRange, setTempCompareRange] = useState(compareValue);
    const [currentMonth1, setCurrentMonth1] = useState(() => {
        const now = today(getLocalTimeZone());
        return startOfMonth(now);
    });
    const [currentMonth2, setCurrentMonth2] = useState(() => {
        const now = today(getLocalTimeZone());
        return startOfMonth(now.add({ months: 1 }));
    });
    const [selectedHoliday, setSelectedHoliday] = useState(null);
    const [selectingCompare, setSelectingCompare] = useState(false);
    const [isCustomCompareSelected, setIsCustomCompareSelected] = useState(false);
    const [activeComparePreset, setActiveComparePreset] = useState(null);
    const [activePreset, setActivePreset] = useState(null);
    const [activeComparePresetUI, setActiveComparePresetUI] = useState(null);
    const anchorRef = useRef(null);
    const popperRef = useRef(null);
    const mobileModalRef = useRef(null);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const dispatch = useDispatch();
    const holidays = useSelector(selectHolidays);
    const holidaysLoading = useSelector(selectHolidaysLoading);
    const token = useSelector(selectToken);
    let contextClientId = '';
    try {
        const accountOverview = useAccountOverview();
        contextClientId = accountOverview?.selectedClientId || '';
    }
    catch (e) {
        // context not present
    }
    const effectiveClientId = clientId !== undefined ? clientId : contextClientId;
    // Local state for holidays search, pagination/limit, and loading
    const [localHolidays, setLocalHolidays] = useState([]);
    const [localHolidaysLoading, setLocalHolidaysLoading] = useState(false);
    const [holidaySearchText, setHolidaySearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [holidayLimit, setHolidayLimit] = useState(10);
    const [totalHolidays, setTotalHolidays] = useState(0);
    // Debounce search text
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchText(holidaySearchText);
        }, 300);
        return () => clearTimeout(timer);
    }, [holidaySearchText]);
    // Fetch holidays with local parameters
    const fetchHolidaysData = useCallback(async (search, limitVal) => {
        if (!token)
            return;
        setLocalHolidaysLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('status', 'active');
            params.set('limit', String(limitVal));
            params.set('page', '1');
            params.set('sortBy', 'start_date');
            params.set('sortOrder', 'desc');
            if (search) {
                params.set('searchText', search);
            }
            const res = await ApiCall('GET', `/api/holidays?${params.toString()}`, '', { Authorization: token });
            if (res && 'data' in res && res?.data?.success) {
                const payload = res.data?.data || {};
                const rawHolidays = payload.holidays || [];
                const filtered = rawHolidays.filter((h) => {
                    const hClientId = h?.client_id?._id || h?.client_id;
                    if (hClientId) {
                        return effectiveClientId ? String(hClientId) === String(effectiveClientId) : false;
                    }
                    return true;
                });
                setLocalHolidays(filtered);
                setTotalHolidays(payload.pagination?.total || 0);
            }
            else {
                setLocalHolidays([]);
                setTotalHolidays(0);
            }
        }
        catch (e) {
            console.error('Error fetching calendar holidays:', e);
            setLocalHolidays([]);
            setTotalHolidays(0);
        }
        finally {
            setLocalHolidaysLoading(false);
        }
    }, [token, effectiveClientId]);
    // Fetch when isOpen, tab is active, search text changes, or limit changes
    useEffect(() => {
        if (isOpen && activeTab === 1) {
            fetchHolidaysData(debouncedSearchText, holidayLimit);
        }
    }, [isOpen, activeTab, debouncedSearchText, holidayLimit, fetchHolidaysData]);
    // Helper to extract year from holiday start_date
    const getHolidayYear = (holiday) => {
        if (!holiday.start_date)
            return '';
        try {
            const d = new Date(holiday.start_date);
            if (!isNaN(d.getTime())) {
                return d.getFullYear().toString();
            }
        }
        catch (e) { }
        return '';
    };
    const parseToCalendarDate = (dateStr) => {
        const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr.split(' ')[0];
        const parts = datePart.split(/[-/]/).map(Number);
        let year, month, day;
        if (parts[0] > 1000) {
            [year, month, day] = parts;
        }
        else {
            [month, day, year] = parts;
        }
        return new CalendarDate(year, month, day);
    };
    const getMaxSelectableDate = () => {
        const now = today(getLocalTimeZone());
        return now.add({ years: maxNextYears }); // Allow dates up to maxNextYears in the future
    };
    const getDefaultMainPreset = () => {
        const maxDate = getMaxSelectableDate();
        return maxDate.day === 1 ? 'Last Month' : 'This Month';
    };
    const predefinedRanges = [
        {
            label: 'Yesterday',
            getRange: () => {
                const now = getMaxSelectableDate();
                const yesterday = today(getLocalTimeZone()).subtract({ days: 1 });
                return { start: yesterday, end: yesterday };
            },
        },
        {
            label: 'This Week',
            getRange: () => {
                const now = today(getLocalTimeZone());
                const yesterday = now.subtract({ days: 1 });
                const weekStart = startOfWeek(now, 'en-US');
                if (weekStart.compare(now) === 0) {
                    // If today is the start of the week, MTD-1 for the week is last week or same day
                    return null;
                }
                return {
                    start: weekStart,
                    end: yesterday,
                };
            },
        },
        {
            label: 'Last 7 Days',
            getRange: () => {
                const yesterday = today(getLocalTimeZone()).subtract({ days: 1 });
                return {
                    start: yesterday.subtract({ days: 6 }),
                    end: yesterday,
                };
            },
        },
        {
            label: 'Last 28 Days',
            getRange: () => {
                const yesterday = today(getLocalTimeZone()).subtract({ days: 1 });
                return {
                    start: yesterday.subtract({ days: 27 }),
                    end: yesterday,
                };
            },
        },
        {
            label: 'Last 30 Days',
            getRange: () => {
                const yesterday = today(getLocalTimeZone()).subtract({ days: 1 });
                return {
                    start: yesterday.subtract({ days: 29 }),
                    end: yesterday,
                };
            },
        },
        {
            label: 'Last 60 Days',
            getRange: () => {
                const yesterday = today(getLocalTimeZone()).subtract({ days: 1 });
                return { start: yesterday.subtract({ days: 59 }), end: yesterday };
            },
        },
        {
            label: 'Last 90 Days',
            getRange: () => {
                const yesterday = today(getLocalTimeZone()).subtract({ days: 1 });
                return {
                    start: yesterday.subtract({ days: 89 }),
                    end: yesterday,
                };
            },
        },
        {
            label: 'Last 120 Days',
            getRange: () => {
                const yesterday = today(getLocalTimeZone()).subtract({ days: 1 });
                return { start: yesterday.subtract({ days: 119 }), end: yesterday };
            },
        },
        {
            label: 'This Month',
            getRange: () => {
                const now = today(getLocalTimeZone());
                const yesterday = now.subtract({ days: 1 });
                return {
                    start: startOfMonth(now),
                    end: yesterday.month === now.month ? yesterday : now,
                };
            },
        },
        {
            label: 'Last Month',
            getRange: () => {
                const now = today(getLocalTimeZone());
                const lastMonth = now.subtract({ months: 1 });
                return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
            },
        },
        {
            label: 'This Year',
            getRange: () => {
                const now = today(getLocalTimeZone());
                const yesterday = now.subtract({ days: 1 });
                return {
                    start: new CalendarDate(now.year, 1, 1),
                    end: yesterday.year === now.year ? yesterday : now,
                };
            },
        },
        {
            label: 'Last 12 Months',
            getRange: () => {
                const yesterday = today(getLocalTimeZone()).subtract({ days: 1 });
                return {
                    start: yesterday.subtract({ months: 11 }),
                    end: yesterday,
                };
            },
        },
        {
            label: 'Last Calendar Year',
            getRange: () => {
                const now = today(getLocalTimeZone());
                const year = now.year;
                return {
                    start: new CalendarDate(year - 1, 1, 1),
                    end: new CalendarDate(year - 1, 12, 31),
                };
            },
        },
        {
            label: 'All Time',
            getRange: () => {
                // Jan 1, 2024 (start of the portal's data) to yesterday.
                const yesterday = today(getLocalTimeZone()).subtract({ days: 1 });
                return { start: new CalendarDate(2024, 1, 1), end: yesterday };
            },
        },
        // Pick any start and end on the calendar; also shown as selected when the range matches no preset.
        { label: CUSTOM_RANGE, isCustom: true, getRange: () => null },
    ];
    // Compare range presets - these calculate based on the selected date range
    const getCompareRanges = () => {
        if (!tempDateRange || !tempDateRange.start || !tempDateRange.end) {
            // If no date range selected, show basic options
            return [
                {
                    label: 'Month-over-Month',
                    getRange: () => null,
                    requiresDateRange: true,
                },
                {
                    label: 'Previous Year',
                    getRange: () => null,
                    requiresDateRange: true,
                },
                {
                    label: 'Previous Period',
                    getRange: () => null,
                    requiresDateRange: true,
                },
                {
                    label: 'Custom',
                    getRange: () => null,
                    isCustom: true,
                },
            ];
        }
        const mainStart = tempDateRange.start;
        const mainEnd = tempDateRange.end;
        return [
            // Month-over-Month
            {
                label: 'Month-over-Month',
                getRange: () => {
                    let newStart = mainStart.subtract({ months: 1 });
                    let newEnd = mainEnd.subtract({ months: 1 });
                    // If the selected start is the start of its month, keep it the start of the new month
                    if (mainStart.compare(startOfMonth(mainStart)) === 0) {
                        newStart = startOfMonth(newStart);
                    }
                    // If the selected end is the end of its month, keep it the end of the new month
                    if (mainEnd.compare(endOfMonth(mainEnd)) === 0) {
                        newEnd = endOfMonth(newEnd);
                    }
                    return {
                        start: newStart,
                        end: newEnd,
                    };
                },
            },
            // Previous Year
            {
                label: 'Previous Year',
                getRange: () => {
                    let newStart = mainStart.subtract({ years: 1 });
                    let newEnd = mainEnd.subtract({ years: 1 });
                    // Ensure end-of-month aligns for leap years etc.
                    if (mainStart.compare(startOfMonth(mainStart)) === 0) {
                        newStart = startOfMonth(newStart);
                    }
                    if (mainEnd.compare(endOfMonth(mainEnd)) === 0) {
                        newEnd = endOfMonth(newEnd);
                    }
                    return {
                        start: newStart,
                        end: newEnd,
                    };
                },
            },
            {
                label: 'Previous Period',
                getRange: () => {
                    const mainStart = tempDateRange.start;
                    const mainEnd = tempDateRange.end;
                    // ✅ CORRECT duration calculation
                    const startJS = mainStart.toDate(getLocalTimeZone());
                    const endJS = mainEnd.toDate(getLocalTimeZone());
                    const duration = Math.round((endJS.getTime() - startJS.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                    // ✅ Previous Period
                    const compareEnd = mainStart.subtract({ days: 1 });
                    const compareStart = compareEnd.subtract({ days: duration - 1 });
                    return {
                        start: compareStart,
                        end: compareEnd,
                    };
                },
            },
            {
                label: 'Custom',
                getRange: () => {
                    // Custom - user will select manually
                    return null;
                },
                isCustom: true,
            },
        ];
    };
    useEffect(() => {
        if (value) {
            setTempDateRange(value);
            if (value.start) {
                const startMonth = startOfMonth(value.start);
                setCurrentMonth1(startMonth);
                setCurrentMonth2(startMonth.add({ months: 1 }));
            }
        }
    }, [value]);
    // Removed global fetch on mobile mount since we use local holidays state
    useEffect(() => {
        if (compareValue) {
            setTempCompareRange(compareValue);
            setCompareEnabled(true);
        }
        else {
            setTempCompareRange(null);
            setCompareEnabled(false);
        }
    }, [compareValue]);
    const handleOpen = (isCompare = false) => {
        setIsOpen(true);
        if (isCompare && compareEnabled) {
            setSelectingCompare(true);
            setActiveTab(0);
            if (tempCompareRange?.start) {
                const startMonth = startOfMonth(tempCompareRange.start);
                setCurrentMonth1(startMonth);
                setCurrentMonth2(startMonth.add({ months: 1 }));
            }
        }
        else {
            setSelectingCompare(false);
            setActiveTab(0);
            if (tempDateRange?.start) {
                const startMonth = startOfMonth(tempDateRange.start);
                setCurrentMonth1(startMonth);
                setCurrentMonth2(startMonth.add({ months: 1 }));
            }
        }
    };
    useEffect(() => {
        if (!activeComparePreset || !tempDateRange?.start || !tempDateRange?.end)
            return;
        const preset = getCompareRanges().find((p) => p.label === activeComparePreset);
        if (!preset || !preset.getRange)
            return;
        const newCompareRange = preset.getRange();
        if (newCompareRange) {
            setTempCompareRange(newCompareRange);
        }
    }, [tempDateRange?.start, tempDateRange?.end, activeComparePreset]);
    useEffect(() => {
        if (!value) {
            setActivePreset(null);
            return;
        }
        const matchedPreset = predefinedRanges.find((p) => {
            const r = p.getRange();
            return (r?.start?.compare(value.start) === 0 && r?.end?.compare(value.end) === 0);
        });
        setActivePreset(matchedPreset?.label ?? CUSTOM_RANGE);
        // If comparison is enabled, we need to match compare preset independently of main preset
        if (compareEnabled && compareValue) {
            // Find which compare preset matches the current compare value
            const matchedComparePreset = getCompareRanges().find(p => {
                const cr = p.getRange();
                if (!cr)
                    return false;
                return (cr.start?.compare(compareValue.start) === 0 &&
                    cr.end?.compare(compareValue.end) === 0);
            });
            if (matchedComparePreset) {
                setActiveComparePreset(matchedComparePreset.label);
                setActiveComparePresetUI(matchedComparePreset.label);
            }
            else {
                // Fallback for custom
                setActiveComparePresetUI("Custom");
                setIsCustomCompareSelected(true);
            }
        }
        else if (!compareEnabled) {
            setActiveComparePreset(null);
            setActiveComparePresetUI(null);
            setIsCustomCompareSelected(false);
        }
        // compareEnabled but no applied compareValue yet: the toggle has just turned
        // compare on and picked Month-over-Month - keep that, don't wipe it.
    }, [value, compareValue, compareEnabled, isOpen]);
    const handleClose = useCallback(() => {
        setIsOpen(false);
        setTempDateRange(value);
        setTempCompareRange(compareValue);
        setCompareEnabled(!!compareValue);
        setSelectedHoliday(null);
        setSelectingCompare(false);
        setIsCustomCompareSelected(false);
    }, [value, compareValue]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isOpen)
                return;
            // Mobile modal is fullscreen — no outside exists; Cancel/Apply handle closing
            if (isMobile)
                return;
            const target = event.target;
            const isOutsideTrigger = !anchorRef.current?.contains(target);
            const isOutsidePopper = !popperRef.current?.contains(target);
            if (isOutsideTrigger && isOutsidePopper) {
                handleClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, isMobile, handleClose]);
    const handleApply = () => {
        onChange(tempDateRange);
        if (compareEnabled && tempCompareRange) {
            onCompareChange(tempCompareRange);
        }
        else {
            onCompareChange(null);
        }
        onApply?.({
            mainRange: tempDateRange,
            compareRange: compareEnabled ? tempCompareRange : null,
        });
        setIsOpen(false);
        setSelectedHoliday(null);
        setSelectingCompare(false);
        setIsCustomCompareSelected(false);
    };
    const handleCancel = () => {
        handleClose();
    };
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setSelectedHoliday(null);
        if (newValue === 1) {
            setHolidaySearchText('');
            setHolidayLimit(10);
        }
    };
    const handleCompareToggle = (event) => {
        const enabled = event.target.checked;
        setCompareEnabled(enabled);
        if (!enabled) {
            setTempCompareRange(null);
            setSelectingCompare(false);
            setActiveComparePreset(null);
            setActiveComparePresetUI(null);
            setIsCustomCompareSelected(false);
            return;
        }
        // 🔹 Compare ON → default MOM
        if (tempDateRange?.start && tempDateRange?.end) {
            const momPreset = getCompareRanges().find((p) => p.label === 'Month-over-Month');
            if (momPreset) {
                const range = momPreset.getRange();
                if (range) {
                    setTempCompareRange(range);
                    setActiveComparePreset('Month-over-Month');
                    setActiveComparePresetUI('Month-over-Month');
                    setIsCustomCompareSelected(false);
                    const startMonth = startOfMonth(range.start);
                    setCurrentMonth1(startMonth);
                    setCurrentMonth2(startMonth.add({ months: 1 }));
                }
            }
        }
        setSelectingCompare(true);
        setActiveTab(0);
    };
    const handlePresetClick = (preset) => {
        if (selectingCompare && compareEnabled) {
            // Handle compare range preset
            if (preset.isCustom) {
                // For custom, just enable manual selection
                setIsCustomCompareSelected(true);
                setSelectingCompare(true);
                // ✅ FIX: Custom ko highlight karo
                setActiveComparePresetUI(preset.label);
                setActiveComparePreset(null);
                return;
            }
            setActiveComparePresetUI(preset.label);
            if (!preset.isCustom) {
                setActiveComparePreset(preset.label);
            }
            if (preset.requiresDateRange &&
                (!tempDateRange || !tempDateRange.start || !tempDateRange.end)) {
                // Can't calculate compare range without a date range
                return;
            }
            const range = preset.getRange();
            if (range) {
                setTempCompareRange(range);
                setIsCustomCompareSelected(false); // Reset custom selection when preset is chosen
                if (range.start) {
                    const startMonth = startOfMonth(range.start);
                    setCurrentMonth1(startMonth);
                    setCurrentMonth2(startMonth.add({ months: 1 }));
                }
            }
        }
        else {
            // Handle main date range preset
            setActivePreset(preset.label);
            if (preset.isCustom)
                return; // Custom Range: the person picks start and end on the calendar
            const range = preset.getRange();
            if (range) {
                setTempDateRange(range);
                // If compare is enabled but no compare preset is active, default to Month-over-Month
                if (compareEnabled && !activeComparePreset) {
                    setActiveComparePreset('Month-over-Month');
                    setActiveComparePresetUI('Month-over-Month');
                    setIsCustomCompareSelected(false);
                }
                if (range.start) {
                    const startMonth = startOfMonth(range.start);
                    setCurrentMonth1(startMonth);
                    setCurrentMonth2(startMonth.add({ months: 1 }));
                }
            }
        }
    };
    const handleHolidayClick = (holiday) => {
        const start = parseToCalendarDate(holiday.start_date);
        const end = parseToCalendarDate(holiday.end_date);
        setSelectedHoliday({
            ...holiday,
            date: start
        });
        setTempDateRange({ start, end });
        const startMonth = startOfMonth(start);
        setCurrentMonth1(startMonth);
        setCurrentMonth2(startMonth.add({ months: 1 }));
    };
    const handleDateClick = (date, calendarIndex) => {
        if (selectingCompare && compareEnabled) {
            // Selecting compare range - only allow if custom is selected
            if (!isCustomCompareSelected) {
                return; // Don't allow date selection unless custom compare is selected
            }
            if (!tempCompareRange || !tempCompareRange.start) {
                // First click: set start date, auto-set end to same date
                setTempCompareRange({ start: date, end: date });
            }
            else if (tempCompareRange.start && tempCompareRange.end) {
                // Both dates exist - check if they're the same (auto-set end)
                if (tempCompareRange.start.compare(tempCompareRange.end) === 0) {
                    // End was auto-set, so change end date only
                    if (date.compare(tempCompareRange.start) < 0) {
                        setTempCompareRange({ start: date, end: tempCompareRange.start });
                    }
                    else {
                        setTempCompareRange({ start: tempCompareRange.start, end: date });
                    }
                }
                else {
                    // End was manually set, start new selection
                    setTempCompareRange({ start: date, end: date });
                }
            }
        }
        else {
            // Selecting main date range
            if (!tempDateRange || !tempDateRange.start) {
                // First click: set start date, auto-set end to same date
                setTempDateRange({ start: date, end: date });
            }
            else if (tempDateRange.start && tempDateRange.end) {
                // Both dates exist - check if they're the same (auto-set end)
                if (tempDateRange.start.compare(tempDateRange.end) === 0) {
                    // End was auto-set, so change end date only
                    if (date.compare(tempDateRange.start) < 0) {
                        setTempDateRange({ start: date, end: tempDateRange.start });
                    }
                    else {
                        setTempDateRange({ start: tempDateRange.start, end: date });
                    }
                }
                else {
                    // End was manually set, start new selection
                    setTempDateRange({ start: date, end: date });
                }
            }
            // If compare is enabled but no compare preset is active, default to Month-over-Month
            if (compareEnabled && !activeComparePreset) {
                setActiveComparePreset('Month-over-Month');
                setActiveComparePresetUI('Month-over-Month');
                setIsCustomCompareSelected(false);
            }
        }
    };
    const navigateMonth = (direction, calendarIndex) => {
        const currentDate = today(getLocalTimeZone());
        const minAllowedMonth = new CalendarDate(currentDate.year - maxPastYears, 1, 1);
        const maxAllowedDate = currentDate.add({ years: maxNextYears });
        const maxAllowedMonth = startOfMonth(maxAllowedDate);
        // New month calculation
        const newMonth1 = currentMonth1.add({ months: direction });
        const newMonth2 = newMonth1.add({ months: 1 });
        if (!allowAllDates) {
            // Check min boundary (left calendar)
            if (newMonth1.compare(minAllowedMonth) < 0)
                return;
            // Check max boundary
            if (isMobile || size === 'compact') {
                if (newMonth1.compare(maxAllowedMonth) > 0)
                    return;
            }
            else {
                if (newMonth2.compare(maxAllowedMonth) > 0)
                    return;
            }
        }
        // Update both calendars together
        setCurrentMonth1(newMonth1);
        setCurrentMonth2(newMonth2);
    };
    // Check if previous navigation should be disabled
    const isPreviousDisabled = () => {
        if (allowAllDates)
            return false;
        const currentDate = today(getLocalTimeZone());
        const minAllowedMonth = new CalendarDate(currentDate.year - maxPastYears, 1, 1);
        return currentMonth1.compare(minAllowedMonth) <= 0;
    };
    // Check if next navigation should be disabled
    const isNextDisabled = () => {
        if (allowAllDates)
            return false;
        const currentDate = today(getLocalTimeZone());
        const maxAllowedDate = currentDate.add({ years: maxNextYears });
        const maxAllowedMonth = startOfMonth(maxAllowedDate);
        if (size === 'compact') {
            return currentMonth1.compare(maxAllowedMonth) >= 0;
        }
        return currentMonth2.compare(maxAllowedMonth) >= 0;
    };
    const formatDateRange = (range) => {
        if (!range || !range.start)
            return '';
        if (!range.end)
            return formatDate(range.start);
        return `${formatDate(range.start)} - ${formatDate(range.end)}`;
    };
    const formatDate = (date) => {
        if (!date)
            return '';
        const months = [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];
        const monthName = months[date.month - 1];
        return `${monthName} ${date.day}, ${date.year}`;
    };
    const renderCalendar = (month, calendarIndex) => {
        const start = startOfMonth(month);
        const end = endOfMonth(month);
        const startWeek = startOfWeek(start, 'en-US');
        // ✅ FIX: Always generate exactly 42 days (6 weeks × 7 days)
        const days = [];
        let current = startWeek;
        // Generate exactly 42 days for consistent 6-row grid
        for (let i = 0; i < 42; i++) {
            days.push(current);
            current = current.add({ days: 1 });
        }
        const activeRange = selectingCompare && compareEnabled ? tempCompareRange : tempDateRange;
        const showOtherRange = compareEnabled && selectingCompare;
        const otherRange = showOtherRange ? tempCompareRange : null;
        const monthName = new Date(month.year, month.month - 1, month.day).toLocaleString('en-US', { month: 'long', year: 'numeric' });
        const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return (_jsxs(Box, { className: 'main-calendar-container', children: [_jsxs(Box, { className: 'calendar-header', children: [calendarIndex === 0 ? (_jsx(IconButton, { size: 'small', onClick: () => navigateMonth(-1, calendarIndex), disabled: isMobile
                                ? false
                                : isPreviousDisabled(), className: 'calendar-nav-button', children: _jsx(ChevronLeft, {}) })) : (_jsx(Box, { sx: { width: 28, height: 28 } })), _jsx(Typography, { variant: 'body1', className: 'calendar-month-name', children: monthName }), calendarIndex === 1 || (isCompact && calendarIndex === 0) || (isMobile && calendarIndex === 0) ? (_jsx(IconButton, { size: 'small', onClick: () => navigateMonth(1, calendarIndex), disabled: isMobile
                                ? false
                                : isNextDisabled(), className: 'calendar-nav-button', children: _jsx(ChevronRight, {}) })) : (_jsx(Box, { sx: { width: 28, height: 28 } }))] }), _jsxs(Box, { className: 'calendar-grid', children: [weekDays.map((day) => (_jsx(Box, { className: 'calendar-weekday', children: day }, day))), days.map((date, idx) => {
                            const isCurrentMonth = date.month === month.month;
                            const isActiveSelected = activeRange &&
                                activeRange.start &&
                                isSameDate(date, activeRange.start);
                            const isActiveEndSelected = activeRange &&
                                activeRange.end &&
                                isSameDate(date, activeRange.end);
                            const isActiveInRange = activeRange &&
                                activeRange.start &&
                                activeRange.end &&
                                date.compare(activeRange.start) >= 0 &&
                                date.compare(activeRange.end) <= 0;
                            const isOtherInRange = showOtherRange &&
                                otherRange &&
                                otherRange.start &&
                                otherRange.end &&
                                date.compare(otherRange.start) >= 0 &&
                                date.compare(otherRange.end) <= 0;
                            const isHolidaySelected = !selectingCompare &&
                                selectedHoliday &&
                                selectedHoliday.date &&
                                isSameDate(date, selectedHoliday.date);
                            const isDisabled = isFutureDate(date);
                            const isCompareModeDisabled = selectingCompare && compareEnabled && !isCustomCompareSelected;
                            return (_jsx(Box, { className: `calendar-day 
                ${!isCurrentMonth ? 'other-month' : ''} 
                ${isActiveSelected || isActiveEndSelected ? 'selected' : ''} 
                ${isActiveInRange ? 'in-range' : ''} 
                ${isOtherInRange && !isActiveInRange ? 'compare-range' : ''} 
                ${isHolidaySelected ? 'holiday-selected' : ''} 
                ${isDisabled || isCompareModeDisabled ? 'disabled-date' : ''}`, onClick: () => isCurrentMonth &&
                                    !isDisabled &&
                                    !isCompareModeDisabled &&
                                    handleDateClick(date, calendarIndex), children: date.day }, idx));
                        })] })] }));
    };
    const isSameDate = (date1, date2) => {
        if (!date1 || !date2)
            return false;
        return (date1.year === date2.year &&
            date1.month === date2.month &&
            date1.day === date2.day);
    };
    const isFutureDate = (date) => {
        if (allowAllDates)
            return false;
        const now = today(getLocalTimeZone());
        // Today is unavailable by default (reports cap at yesterday); allowToday opts in to it.
        if (date.compare(now) === 0)
            return !allowToday;
        const maxDate = getMaxSelectableDate();
        return date.compare(maxDate) > 0;
    };
    const displayValue = formatDateRange(value);
    const displayCompareValue = formatDateRange(compareValue);
    const isCompact = size === 'compact';
    return (_jsxs(Box, { className: 'custom-date-picker-wrapper', children: [_jsxs(Box, { className: 'date-picker-trigger-container', ref: anchorRef, children: [_jsxs(Box, { className: `date-picker-trigger-field 
${disabled ? 'disabled-trigger' : ''}
${isOpen && !selectingCompare ? 'active-trigger' : ''}`, onClick: () => {
                            if (!disabled) {
                                if (isOpen && !selectingCompare) {
                                    handleClose();
                                }
                                else {
                                    handleOpen(false);
                                }
                            }
                        }, children: [_jsx("div", { className: 'd-flex', children: _jsx("svg", { className: 'calendar-icon', width: '18', height: '18', viewBox: '0 0 18 18', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', children: _jsx("path", { d: 'M12.75 1.5C12.75 1.08579 12.4142 0.75 12 0.75C11.5858 0.75 11.25 1.08579 11.25 1.5H12H12.75ZM11.25 4.5C11.25 4.91421 11.5858 5.25 12 5.25C12.4142 5.25 12.75 4.91421 12.75 4.5H12H11.25ZM6.75 1.5C6.75 1.08579 6.41421 0.75 6 0.75C5.58579 0.75 5.25 1.08579 5.25 1.5H6H6.75ZM5.25 4.5C5.25 4.91421 5.58579 5.25 6 5.25C6.41421 5.25 6.75 4.91421 6.75 4.5H6H5.25ZM2.25 6.75C1.83579 6.75 1.5 7.08579 1.5 7.5C1.5 7.91421 1.83579 8.25 2.25 8.25V7.5V6.75ZM15.75 8.25C16.1642 8.25 16.5 7.91421 16.5 7.5C16.5 7.08579 16.1642 6.75 15.75 6.75V7.5V8.25ZM3.75 3V3.75H14.25V3V2.25H3.75V3ZM14.25 3V3.75C14.6642 3.75 15 4.08579 15 4.5H15.75H16.5C16.5 3.25736 15.4926 2.25 14.25 2.25V3ZM15.75 4.5H15V15H15.75H16.5V4.5H15.75ZM15.75 15H15C15 15.4142 14.6642 15.75 14.25 15.75V16.5V17.25C15.4926 17.25 16.5 16.2426 16.5 15H15.75ZM14.25 16.5V15.75H3.75V16.5V17.25H14.25V16.5ZM3.75 16.5V15.75C3.33579 15.75 3 15.4142 3 15H2.25H1.5C1.5 16.2426 2.50736 17.25 3.75 17.25V16.5ZM2.25 15H3V4.5H2.25H1.5V15H2.25ZM2.25 4.5H3C3 4.08579 3.33579 3.75 3.75 3.75V3V2.25C2.50736 2.25 1.5 3.25736 1.5 4.5H2.25ZM12 1.5H11.25V4.5H12H12.75V1.5H12ZM6 1.5H5.25V4.5H6H6.75V1.5H6ZM2.25 7.5V8.25H15.75V7.5V6.75H2.25V7.5Z', fill: '#71717A' }) }) }), _jsx("span", { className: `date-display-text ${!displayValue ? 'placeholder-text' : ''}`, children: displayValue || placeholder })] }), compareEnabled && (_jsxs(_Fragment, { children: [_jsx("span", { className: 'date-picker-trigger-field  vs-separator-trigger', children: "Vs" }), _jsxs(Box, { className: `date-picker-trigger-field 
${disabled ? 'disabled-trigger' : ''}
${isOpen && !selectingCompare ? 'active-trigger' : ''}`, onClick: () => {
                                    if (isOpen && selectingCompare) {
                                        handleClose();
                                    }
                                    else {
                                        handleOpen(true);
                                    }
                                }, children: [_jsx("div", { className: 'd-flex', children: _jsx("svg", { className: 'calendar-icon', width: '18', height: '18', viewBox: '0 0 18 18', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', children: _jsx("path", { d: 'M12.75 1.5C12.75 1.08579 12.4142 0.75 12 0.75C11.5858 0.75 11.25 1.08579 11.25 1.5H12H12.75ZM11.25 4.5C11.25 4.91421 11.5858 5.25 12 5.25C12.4142 5.25 12.75 4.91421 12.75 4.5H12H11.25ZM6.75 1.5C6.75 1.08579 6.41421 0.75 6 0.75C5.58579 0.75 5.25 1.08579 5.25 1.5H6H6.75ZM5.25 4.5C5.25 4.91421 5.58579 5.25 6 5.25C6.41421 5.25 6.75 4.91421 6.75 4.5H6H5.25ZM2.25 6.75C1.83579 6.75 1.5 7.08579 1.5 7.5C1.5 7.91421 1.83579 8.25 2.25 8.25V7.5V6.75ZM15.75 8.25C16.1642 8.25 16.5 7.91421 16.5 7.5C16.5 7.08579 16.1642 6.75 15.75 6.75V7.5V8.25ZM3.75 3V3.75H14.25V3V2.25H3.75V3ZM14.25 3V3.75C14.6642 3.75 15 4.08579 15 4.5H15.75H16.5C16.5 3.25736 15.4926 2.25 14.25 2.25V3ZM15.75 4.5H15V15H15.75H16.5V4.5H15.75ZM15.75 15H15C15 15.4142 14.6642 15.75 14.25 15.75V16.5V17.25C15.4926 17.25 16.5 16.2426 16.5 15H15.75ZM14.25 16.5V15.75H3.75V16.5V17.25H14.25V16.5ZM3.75 16.5V15.75C3.33579 15.75 3 15.4142 3 15H2.25H1.5C1.5 16.2426 2.50736 17.25 3.75 17.25V16.5ZM2.25 15H3V4.5H2.25H1.5V15H2.25ZM2.25 4.5H3C3 4.08579 3.33579 3.75 3.75 3.75V3V2.25C2.50736 2.25 1.5 3.25736 1.5 4.5H2.25ZM12 1.5H11.25V4.5H12H12.75V1.5H12ZM6 1.5H5.25V4.5H6H6.75V1.5H6ZM2.25 7.5V8.25H15.75V7.5V6.75H2.25V7.5Z', fill: '#71717A' }) }) }), _jsx("span", { className: `date-display-text ${!displayCompareValue ? 'placeholder-text' : ''}`, children: displayCompareValue || comparePlaceholder })] })] }))] }), isOpen && !isMobile && (_jsx(Popper, { open: isOpen, anchorEl: anchorRef.current, placement: "bottom-start", ref: popperRef, className: "date-picker-popper", disablePortal: false, popperOptions: { strategy: popperStrategy }, modifiers: [
                    {
                        name: "offset",
                        options: { offset: [0, 8] },
                    },
                    {
                        name: "preventOverflow",
                        options: {
                            boundary: "viewport",
                            padding: 16,
                            altAxis: true,
                            tether: false,
                        },
                    },
                    {
                        name: "flip",
                        options: {
                            fallbackPlacements: ["top-start", "bottom-end", "top-end"],
                            padding: 16,
                        },
                    },
                    {
                        name: "size",
                        enabled: true,
                        phase: "beforeWrite",
                        requires: ["computeStyles"],
                        fn: ({ state }) => {
                            const maxHeight = window.innerHeight - 32;
                            state.styles.popper.maxHeight = `${maxHeight}px`;
                            state.styles.popper.overflow = "auto";
                        },
                    },
                ], children: _jsx(Paper, { className: `date-picker-modal ${isCompact ? "is-compact" : ""}`, elevation: 8, sx: isCompact
                        ? {
                            width: "100%",
                            maxWidth: 320,
                            "& .calendars-container": { flexWrap: "nowrap", gap: 8 },
                            "& .main-calendar-container": { flex: 1, minWidth: 0 },
                        }
                        : undefined, children: _jsxs(Box, { className: 'date-picker-content', children: [comparison && (_jsxs(Box, { className: 'date-picker-tabs', children: [_jsxs(Tabs, { value: activeTab, onChange: handleTabChange, className: 'custom-tabs', children: [_jsx(Tab, { label: 'Default', value: 0, className: 'custom-tab' }), !selectingCompare && _jsx(Tab, { label: 'Holiday', value: 1, className: 'custom-tab' })] }), _jsx(Box, { className: 'compare-toggle-container', children: _jsx(FormControlLabel, { control: _jsx(Switch, { checked: compareEnabled, onChange: handleCompareToggle, className: 'compare-switch' }), label: 'Compare', className: 'compare-label' }) })] })), _jsxs(Box, { className: 'date-picker-body', children: [!isCompact && (_jsxs(Box, { className: 'date-picker-sidebar', sx: { display: 'flex', flexDirection: 'column', p: 0 }, children: [activeTab === 1 && (_jsx(Box, { sx: { p: '10px 10px 4px 10px', background: '#fafafa', borderBottom: '1px solid #E9EAEB' }, children: _jsx(TextField, { size: "small", placeholder: "Search holidays...", value: holidaySearchText, onChange: (e) => {
                                                        setHolidaySearchText(e.target.value);
                                                        setHolidayLimit(10);
                                                    }, fullWidth: true, variant: "outlined", InputProps: {
                                                        sx: {
                                                            fontSize: '13px',
                                                            height: '32px',
                                                            borderRadius: '6px',
                                                            backgroundColor: '#ffffff',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#E9EAEB',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#D5D7DA',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#FF008E',
                                                            },
                                                        }
                                                    } }) })), _jsx(Box, { sx: { flex: '1 1 auto', position: 'relative', minHeight: 120 }, children: _jsx(Box, { sx: { position: 'absolute', inset: 0, overflowY: 'auto', p: '10px' }, children: activeTab === 0 ? (_jsx(List, { className: 'preset-list', children: (selectingCompare && compareEnabled ? getCompareRanges() : predefinedRanges).map((preset, idx) => {
                                                            const rangeWeek = preset.getRange();
                                                            const isDisabledWeek = rangeWeek === null && !preset.isCustom;
                                                            return (_jsx(ListItem, { disablePadding: true, children: _jsx(ListItemButton, { disabled: isDisabledWeek, onClick: () => !isDisabledWeek && handlePresetClick(preset), className: `preset-item ${((!selectingCompare && activePreset === preset.label) || (selectingCompare && activeComparePresetUI === preset.label)) ? 'active-preset' : ''}`, sx: { textAlign: 'left !important', justifyContent: 'flex-start !important', width: '100%' }, children: _jsx(Box, { sx: { textAlign: 'left', width: '100%' }, children: preset.label }) }) }, idx));
                                                        }) })) : (_jsx(List, { className: 'holiday-list', children: localHolidaysLoading && localHolidays.length === 0 ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', p: 2 }, children: _jsx(CircularProgress, { size: 24, sx: { color: '#FF008E' } }) })) : localHolidays.length === 0 ? (_jsx(ListItem, { children: _jsx(Typography, { variant: "body2", color: "textSecondary", children: "No holidays found" }) })) : localHolidays.map((holiday, idx) => (_jsx(ListItem, { disablePadding: true, children: _jsx(ListItemButton, { onClick: () => {
                                                                    const h = holiday;
                                                                    if (h.start_date && h.end_date)
                                                                        handleHolidayClick(h);
                                                                }, className: `holiday-item ${selectedHoliday?._id === holiday._id ? 'selected' : ''}`, sx: { textTransform: 'capitalize', textAlign: 'left !important', justifyContent: 'flex-start !important', width: '100%' }, children: _jsxs(Box, { sx: { textAlign: 'left', width: '100%' }, children: [holiday.name, " ", getHolidayYear(holiday) ? `- ${getHolidayYear(holiday)}` : ''] }) }) }, idx))) })) }) }), activeTab === 1 && (_jsxs(Box, { sx: { p: '8px 10px', background: '#fafafa', borderTop: '1px solid #E9EAEB', display: 'flex', flexDirection: 'column', alignItems: 'center' }, children: [localHolidaysLoading && localHolidays.length > 0 && (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', p: 0.5 }, children: _jsx(CircularProgress, { size: 16, sx: { color: '#FF008E' } }) })), !localHolidaysLoading && localHolidays.length < totalHolidays && (_jsx(Button, { size: "small", onClick: () => setHolidayLimit(prev => prev + 10), fullWidth: true, sx: {
                                                            textTransform: 'none',
                                                            color: '#FF008E',
                                                            fontSize: '12px',
                                                            fontWeight: 500,
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(255, 0, 142, 0.04)',
                                                            }
                                                        }, children: "Show More" }))] }))] })), _jsxs(Box, { className: 'date-picker-main', children: [_jsxs(Box, { className: 'date-range-inputs', children: [_jsx(TextField, { value: formatDateRange(tempDateRange), placeholder: placeholder, InputProps: {
                                                            readOnly: true,
                                                            startAdornment: (_jsx("div", { className: 'd-flex', children: _jsx("svg", { className: 'input-icon', width: '18', height: '18', viewBox: '0 0 18 18', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', children: _jsx("path", { d: 'M12.75 1.5C12.75 1.08579 12.4142 0.75 12 0.75C11.5858 0.75 11.25 1.08579 11.25 1.5H12H12.75ZM11.25 4.5C11.25 4.91421 11.5858 5.25 12 5.25C12.4142 5.25 12.75 4.91421 12.75 4.5H12H11.25ZM6.75 1.5C6.75 1.08579 6.41421 0.75 6 0.75C5.58579 0.75 5.25 1.08579 5.25 1.5H6H6.75ZM5.25 4.5C5.25 4.91421 5.58579 5.25 6 5.25C6.41421 5.25 6.75 4.91421 6.75 4.5H6H5.25ZM2.25 6.75C1.83579 6.75 1.5 7.08579 1.5 7.5C1.5 7.91421 1.83579 8.25 2.25 8.25V7.5V6.75ZM15.75 8.25C16.1642 8.25 16.5 7.91421 16.5 7.5C16.5 7.08579 16.1642 6.75 15.75 6.75V7.5V8.25ZM3.75 3V3.75H14.25V3V2.25H3.75V3ZM14.25 3V3.75C14.6642 3.75 15 4.08579 15 4.5H15.75H16.5C16.5 3.25736 15.4926 2.25 14.25 2.25V3ZM15.75 4.5H15V15H15.75H16.5V4.5H15.75ZM15.75 15H15C15 15.4142 14.6642 15.75 14.25 15.75V16.5V17.25C15.4926 17.25 16.5 16.2426 16.5 15H15.75ZM14.25 16.5V15.75H3.75V16.5V17.25H14.25V16.5ZM3.75 16.5V15.75C3.33579 15.75 3 15.4142 3 15H2.25H1.5C1.5 16.2426 2.50736 17.25 3.75 17.25V16.5ZM2.25 15H3V4.5H2.25H1.5V15H2.25ZM2.25 4.5H3C3 4.08579 3.33579 3.75 3.75 3.75V3V2.25C2.50736 2.25 1.5 3.25736 1.5 4.5H2.25ZM12 1.5H11.25V4.5H12H12.75V1.5H12ZM6 1.5H5.25V4.5H6H6.75V1.5H6ZM2.25 7.5V8.25H15.75V7.5V6.75H2.25V7.5Z', fill: '#71717A' }) }) })),
                                                        }, className: `range-input-field ${!selectingCompare ? 'active-input' : ''}`, onClick: () => {
                                                            setSelectingCompare(false);
                                                            setActiveTab(0);
                                                            if (tempDateRange?.start) {
                                                                const startMonth = startOfMonth(tempDateRange.start);
                                                                setCurrentMonth1(startMonth);
                                                                setCurrentMonth2(startMonth.add({ months: 1 }));
                                                            }
                                                        } }), compareEnabled && (_jsxs(_Fragment, { children: [_jsx("span", { className: 'MuiFormControl-root vs-text', children: "Vs" }), _jsx(TextField, { value: formatDateRange(tempCompareRange), placeholder: comparePlaceholder, InputProps: {
                                                                    readOnly: true,
                                                                    startAdornment: (_jsx("div", { className: 'd-flex', children: _jsx("svg", { className: 'input-icon', width: '18', height: '18', viewBox: '0 0 18 18', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', children: _jsx("path", { d: 'M12.75 1.5C12.75 1.08579 12.4142 0.75 12 0.75C11.5858 0.75 11.25 1.08579 11.25 1.5H12H12.75ZM11.25 4.5C11.25 4.91421 11.5858 5.25 12 5.25C12.4142 5.25 12.75 4.91421 12.75 4.5H12H11.25ZM6.75 1.5C6.75 1.08579 6.41421 0.75 6 0.75C5.58579 0.75 5.25 1.08579 5.25 1.5H6H6.75ZM5.25 4.5C5.25 4.91421 5.58579 5.25 6 5.25C6.41421 5.25 6.75 4.91421 6.75 4.5H6H5.25ZM2.25 6.75C1.83579 6.75 1.5 7.08579 1.5 7.5C1.5 7.91421 1.83579 8.25 2.25 8.25V7.5V6.75ZM15.75 8.25C16.1642 8.25 16.5 7.91421 16.5 7.5C16.5 7.08579 16.1642 6.75 15.75 6.75V7.5V8.25ZM3.75 3V3.75H14.25V3V2.25H3.75V3ZM14.25 3V3.75C14.6642 3.75 15 4.08579 15 4.5H15.75H16.5C16.5 3.25736 15.4926 2.25 14.25 2.25V3ZM15.75 4.5H15V15H15.75H16.5V4.5H15.75ZM15.75 15H15C15 15.4142 14.6642 15.75 14.25 15.75V16.5V17.25C15.4926 17.25 16.5 16.2426 16.5 15H15.75ZM14.25 16.5V15.75H3.75V16.5V17.25H14.25V16.5ZM3.75 16.5V15.75C3.33579 15.75 3 15.4142 3 15H2.25H1.5C1.5 16.2426 2.50736 17.25 3.75 17.25V16.5ZM2.25 15H3V4.5H2.25H1.5V15H2.25ZM2.25 4.5H3C3 4.08579 3.33579 3.75 3.75 3.75V3V2.25C2.50736 2.25 1.5 3.25736 1.5 4.5H2.25ZM12 1.5H11.25V4.5H12H12.75V1.5H12ZM6 1.5H5.25V4.5H6H6.75V1.5H6ZM2.25 7.5V8.25H15.75V7.5V6.75H2.25V7.5Z', fill: '#71717A' }) }) })),
                                                                }, className: `range-input-field ${selectingCompare ? 'active-input' : ''}`, onClick: () => {
                                                                    setSelectingCompare(true);
                                                                    setActiveTab(0);
                                                                    if (tempCompareRange?.start) {
                                                                        const startMonth = startOfMonth(tempCompareRange.start);
                                                                        setCurrentMonth1(startMonth);
                                                                        setCurrentMonth2(startMonth.add({ months: 1 }));
                                                                    }
                                                                    else if (tempDateRange?.start) {
                                                                        const startMonth = startOfMonth(tempDateRange.start);
                                                                        setCurrentMonth1(startMonth);
                                                                        setCurrentMonth2(startMonth.add({ months: 1 }));
                                                                    }
                                                                } })] }))] }), _jsxs(Box, { className: 'calendars-container', sx: isCompact
                                                    ? {
                                                        gap: '8px',
                                                        '& .main-calendar-container': { flex: '1 1 0' },
                                                        '& .calendar-header': { padding: '4px 4px' },
                                                        '& .calendar-month-name': { fontSize: '13px !important' },
                                                        '& .calendar-grid': { gap: '3px' },
                                                        '& .calendar-weekday': { fontSize: '11px', padding: '4px 2px' },
                                                        '& .calendar-day': { fontSize: '11px' },
                                                    }
                                                    : undefined, children: [renderCalendar(currentMonth1, 0), !isCompact && renderCalendar(currentMonth2, 1)] }), _jsxs(Box, { className: 'date-picker-actions', children: [_jsx(AppButton, { onClick: handleCancel, className: 'cancel-button', children: "Cancel" }), _jsx(AppButton, { onClick: handleApply, className: 'apply-button', children: applyLabel })] })] })] })] }) }) })), isOpen && isMobile && (_jsx(Box, { className: "mobile-fullscreen-modal", ref: mobileModalRef, children: _jsx(Paper, { className: "date-picker-modal", elevation: 8, square: true, children: _jsxs(Box, { className: 'date-picker-content', children: [comparison && (_jsx(Box, { className: 'date-picker-tabs', children: _jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 16, padding: '0 8px' }, children: [_jsx(Box, { className: 'mobile-preset-row', children: _jsx(Dropdown, { menuHeader: _jsxs(Tabs, { value: activeTab, onChange: (e, val) => {
                                                        // We don't want the dropdown to close or anything, just update the tab
                                                        handleTabChange(e, val);
                                                    }, className: 'custom-tabs', sx: { minHeight: '36px', '& .MuiTab-root': { minHeight: '36px', padding: '6px 12px' } }, children: [_jsx(Tab, { label: 'Default', value: 0, className: 'custom-tab' }), !selectingCompare && _jsx(Tab, { label: 'Holiday', value: 1, className: 'custom-tab' })] }), value: activeTab === 1
                                                    ? (selectedHoliday?._id ?? '')
                                                    : (selectingCompare && compareEnabled
                                                        ? (activeComparePresetUI ?? '')
                                                        : (activePreset ?? '')), onChange: (val) => {
                                                    const v = val;
                                                    if (activeTab === 1) {
                                                        const h = localHolidays.find(hol => hol._id === v);
                                                        if (h)
                                                            handleHolidayClick(h);
                                                    }
                                                    else {
                                                        const allPresets = selectingCompare && compareEnabled ? getCompareRanges() : predefinedRanges;
                                                        const found = allPresets.find(p => p.label === v);
                                                        if (found)
                                                            handlePresetClick(found);
                                                    }
                                                }, options: activeTab === 1
                                                    ? (localHolidaysLoading
                                                        ? [{ value: '', label: 'Loading...' }]
                                                        : localHolidays.map(h => ({
                                                            value: h._id,
                                                            label: `${h.name}${getHolidayYear(h) ? ` - ${getHolidayYear(h)}` : ''}`
                                                        })))
                                                    : (selectingCompare && compareEnabled
                                                        ? getCompareRanges()
                                                        : predefinedRanges).map(p => ({
                                                        value: p.label,
                                                        label: p.label
                                                    })), placeholder: 'Select...', searchable: false, sortByLabel: false, fullWidth: true, size: 'sm', className: 'mobile-preset-dropdown' }) }), _jsx(Box, { className: 'compare-toggle-container', children: _jsx(FormControlLabel, { control: _jsx(Switch, { checked: compareEnabled, onChange: handleCompareToggle, className: 'compare-switch' }), label: 'Compare', className: 'compare-label' }) })] }) })), _jsx(Box, { className: 'date-picker-body', children: _jsxs(Box, { className: 'date-picker-main', children: [_jsxs(Box, { className: 'date-range-inputs', children: [_jsx(TextField, { value: formatDateRange(tempDateRange), placeholder: placeholder, InputProps: {
                                                        readOnly: true,
                                                        startAdornment: (_jsx("div", { className: 'd-flex', children: _jsx("svg", { className: 'input-icon', width: '18', height: '18', viewBox: '0 0 18 18', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', children: _jsx("path", { d: 'M12.75 1.5C12.75 1.08579 12.4142 0.75 12 0.75C11.5858 0.75 11.25 1.08579 11.25 1.5H12H12.75ZM11.25 4.5C11.25 4.91421 11.5858 5.25 12 5.25C12.4142 5.25 12.75 4.91421 12.75 4.5H12H11.25ZM6.75 1.5C6.75 1.08579 6.41421 0.75 6 0.75C5.58579 0.75 5.25 1.08579 5.25 1.5H6H6.75ZM5.25 4.5C5.25 4.91421 5.58579 5.25 6 5.25C6.41421 5.25 6.75 4.91421 6.75 4.5H6H5.25ZM2.25 6.75C1.83579 6.75 1.5 7.08579 1.5 7.5C1.5 7.91421 1.83579 8.25 2.25 8.25V7.5V6.75ZM15.75 8.25C16.1642 8.25 16.5 7.91421 16.5 7.5C16.5 7.08579 16.1642 6.75 15.75 6.75V7.5V8.25ZM3.75 3V3.75H14.25V3V2.25H3.75V3ZM14.25 3V3.75C14.6642 3.75 15 4.08579 15 4.5H15.75H16.5C16.5 3.25736 15.4926 2.25 14.25 2.25V3ZM15.75 4.5H15V15H15.75H16.5V4.5H15.75ZM15.75 15H15C15 15.4142 14.6642 15.75 14.25 15.75V16.5V17.25C15.4926 17.25 16.5 16.2426 16.5 15H15.75ZM14.25 16.5V15.75H3.75V16.5V17.25H14.25V16.5ZM3.75 16.5V15.75C3.33579 15.75 3 15.4142 3 15H2.25H1.5C1.5 16.2426 2.50736 17.25 3.75 17.25V16.5ZM2.25 15H3V4.5H2.25H1.5V15H2.25ZM2.25 4.5H3C3 4.08579 3.33579 3.75 3.75 3.75V3V2.25C2.50736 2.25 1.5 3.25736 1.5 4.5H2.25ZM12 1.5H11.25V4.5H12H12.75V1.5H12ZM6 1.5H5.25V4.5H6H6.75V1.5H6ZM2.25 7.5V8.25H15.75V7.5V6.75H2.25V7.5Z', fill: '#71717A' }) }) })),
                                                    }, className: `range-input-field ${!selectingCompare ? 'active-input' : ''}`, onClick: () => {
                                                        setSelectingCompare(false);
                                                        setActiveTab(0);
                                                        if (tempDateRange?.start) {
                                                            const startMonth = startOfMonth(tempDateRange.start);
                                                            setCurrentMonth1(startMonth);
                                                            setCurrentMonth2(startMonth.add({ months: 1 }));
                                                        }
                                                    } }), compareEnabled && (_jsxs(_Fragment, { children: [_jsx("span", { className: 'MuiFormControl-root vs-text', children: "Vs" }), _jsx(TextField, { value: formatDateRange(tempCompareRange), placeholder: comparePlaceholder, InputProps: {
                                                                readOnly: true,
                                                                startAdornment: (_jsx("div", { className: 'd-flex', children: _jsx("svg", { className: 'input-icon', width: '18', height: '18', viewBox: '0 0 18 18', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', children: _jsx("path", { d: 'M12.75 1.5C12.75 1.08579 12.4142 0.75 12 0.75C11.5858 0.75 11.25 1.08579 11.25 1.5H12H12.75ZM11.25 4.5C11.25 4.91421 11.5858 5.25 12 5.25C12.4142 5.25 12.75 4.91421 12.75 4.5H12H11.25ZM6.75 1.5C6.75 1.08579 6.41421 0.75 6 0.75C5.58579 0.75 5.25 1.08579 5.25 1.5H6H6.75ZM5.25 4.5C5.25 4.91421 5.58579 5.25 6 5.25C6.41421 5.25 6.75 4.91421 6.75 4.5H6H5.25ZM2.25 6.75C1.83579 6.75 1.5 7.08579 1.5 7.5C1.5 7.91421 1.83579 8.25 2.25 8.25V7.5V6.75ZM15.75 8.25C16.1642 8.25 16.5 7.91421 16.5 7.5C16.5 7.08579 16.1642 6.75 15.75 6.75V7.5V8.25ZM3.75 3V3.75H14.25V3V2.25H3.75V3ZM14.25 3V3.75C14.6642 3.75 15 4.08579 15 4.5H15.75H16.5C16.5 3.25736 15.4926 2.25 14.25 2.25V3ZM15.75 4.5H15V15H15.75H16.5V4.5H15.75ZM15.75 15H15C15 15.4142 14.6642 15.75 14.25 15.75V16.5V17.25C15.4926 17.25 16.5 16.2426 16.5 15H15.75ZM14.25 16.5V15.75H3.75V16.5V17.25H14.25V16.5ZM3.75 16.5V15.75C3.33579 15.75 3 15.4142 3 15H2.25H1.5C1.5 16.2426 2.50736 17.25 3.75 17.25V16.5ZM2.25 15H3V4.5H2.25H1.5V15H2.25ZM2.25 4.5H3C3 4.08579 3.33579 3.75 3.75 3.75V3V2.25C2.50736 2.25 1.5 3.25736 1.5 4.5H2.25ZM12 1.5H11.25V4.5H12H12.75V1.5H12ZM6 1.5H5.25V4.5H6H6.75V1.5H6ZM2.25 7.5V8.25H15.75V7.5V6.75H2.25V7.5Z', fill: '#71717A' }) }) })),
                                                            }, className: `range-input-field ${selectingCompare ? 'active-input' : ''}`, onClick: () => {
                                                                setSelectingCompare(true);
                                                                setActiveTab(0);
                                                                if (tempCompareRange?.start) {
                                                                    const startMonth = startOfMonth(tempCompareRange.start);
                                                                    setCurrentMonth1(startMonth);
                                                                    setCurrentMonth2(startMonth.add({ months: 1 }));
                                                                }
                                                                else if (tempDateRange?.start) {
                                                                    const startMonth = startOfMonth(tempDateRange.start);
                                                                    setCurrentMonth1(startMonth);
                                                                    setCurrentMonth2(startMonth.add({ months: 1 }));
                                                                }
                                                            } })] }))] }), _jsx(Box, { className: 'calendars-container', children: renderCalendar(currentMonth1, 0) }), _jsxs(Box, { className: 'date-picker-actions', children: [_jsx(AppButton, { onClick: handleCancel, className: 'cancel-button', children: "Cancel" }), _jsx(AppButton, { onClick: handleApply, className: 'apply-button', children: applyLabel })] })] }) })] }) }) }))] }));
};
export default CustomDatePicker;
